import {
  Card,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { FC } from 'react';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetTaxCodesByCountryPhoenix,
  useGetServiceProviderPhoenix,
  useUpdateServiceProviderPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { ServiceProviderReceiptBatchTaxSelectionView } from '../receipt-batch/form/ServiceProviderReceiptBatchTaxSelection';
import type { TaxItem } from '../receipt-batch/form/ServiceProviderReceiptBatchForm';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ServiceProviderSettingsProps {}

export const ServiceProviderSettings: FC<ServiceProviderSettingsProps> = memo(
  () => {
    const { t } = useTranslation();

    const { data: serviceProvider } = useGetServiceProviderPhoenix();
    const { mutateAsync: updateServiceProvider } =
      useUpdateServiceProviderPhoenix();

    const toggleAutoGenerateReceipts = useCallback(async () => {
      if (!serviceProvider) {
        return;
      }
      await updateServiceProvider({
        default_auto_generate_receipts:
          !serviceProvider.data.default_auto_generate_receipts,
      });
    }, [serviceProvider, updateServiceProvider]);

    const handleTaxItemChange = useCallback(
      async (taxItem: (TaxItem & { id: string | undefined }) | undefined) => {
        if (!serviceProvider) {
          return;
        }
        await updateServiceProvider({
          default_tax_code: taxItem?.id ?? null,
        });
      },
      [serviceProvider, updateServiceProvider]
    );

    if (!serviceProvider || !serviceProvider.data.country_id) {
      return null;
    }

    return (
      <Card sx={{ p: 3, maxWidth: 400 }}>
        <Stack
          direction="column"
          sx={{
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
            }}
          >
            {t('service-provider-app.service-provider-settings.title')}
          </Typography>

          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Stack>
              <Typography variant="caption">
                {t(
                  'service-provider-app.service-provider-settings.auto-generate-receipts-label'
                )}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    name={'autoGenerateReceipts'}
                    checked={
                      serviceProvider.data.default_auto_generate_receipts ??
                      false
                    }
                    onChange={toggleAutoGenerateReceipts}
                  />
                }
                label={
                  serviceProvider.data.default_auto_generate_receipts
                    ? t(
                        'service-provider-app.service-provider-settings.auto-generate-receipts-enabled'
                      )
                    : t(
                        'service-provider-app.service-provider-settings.auto-generate-receipts-disabled'
                      )
                }
              />
            </Stack>
            <Stack>
              <Typography variant="caption">
                {t(
                  'service-provider-app.service-provider-settings.default-tax-rate'
                )}
              </Typography>
              <TaxSelection
                countryId={serviceProvider.data.country_id!}
                taxCodeId={serviceProvider.data.default_tax_code ?? undefined}
                handleTaxItemChange={handleTaxItemChange}
              />
            </Stack>
          </Stack>
        </Stack>
      </Card>
    );
  }
);

interface TaxSelectionProps {
  countryId: string;
  taxCodeId: string | undefined;
  handleTaxItemChange: (
    taxItem: (TaxItem & { id: string | undefined }) | undefined
  ) => void;
}
const TaxSelection: FC<TaxSelectionProps> = ({
  countryId,
  taxCodeId,
  handleTaxItemChange,
}) => {
  const { t } = useTranslation();
  const { data: taxConfigurations } = useGetTaxCodesByCountryPhoenix(countryId);

  const taxItems = useMemo<Array<TaxItem & { id: string | undefined }>>(() => {
    const taxes =
      taxConfigurations?.map<TaxItem & { id: string | undefined }>(
        (taxConfiguration) => ({
          id: taxConfiguration.id,
          taxName: taxConfiguration.name,
          countryId: taxConfiguration.countryId,
          displayExplanation: taxConfiguration.displayTaxRatioExplanation,
          taxAmount: taxConfiguration.taxRatio / 100,
          taxAmountInPercentage: taxConfiguration.taxRatio,
          explanationLong: taxConfiguration.taxRatioExplanation,
          paragraph: taxConfiguration.paragraph,
          // Todo: Currently there is no short explanation in the tax configuration
          explanationShort: undefined,
        })
      ) ?? [];

    return taxes.concat({
      id: undefined,
      taxName: t('service-provider-app.service-provider-settings.no-tax'),
      taxAmountInPercentage: 0,
      countryId: '',
      displayExplanation: true,
      taxAmount: 0,
      explanationLong: undefined,
      explanationShort: undefined,
      hidePercentage: true,
    });
  }, [t, taxConfigurations]);

  const selected = taxItems.find((item) => item.id === taxCodeId);

  return (
    <ServiceProviderReceiptBatchTaxSelectionView<
      TaxItem & { id: string | undefined }
    >
      taxItems={taxItems}
      selectedTaxItem={selected}
      handleTaxItemChange={handleTaxItemChange}
      handleClose={() => {
        return;
      }}
      error={undefined}
      touched={false}
    />
  );
};
